import React from 'react';
import { styles } from './styles';
import SectionWrapper from '../../components/sectionWrapper/sectionWrapper';
import Service from '../../components/service/service';
import { COLORS } from '../../globalStyles';
import { pad2 } from '../../utils';

// Shared wrapper for the service-card icons (18px, accent stroke, rounded caps).
const Icon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    strokeWidth="1.5"
    style={{ stroke: COLORS.accent }}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const IconCode = () => (
  <Icon>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </Icon>
);

const IconAI = () => (
  <Icon>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
  </Icon>
);

const IconGame = () => (
  <Icon>
    {/* Game controller: d-pad + two round buttons + the grip body */}
    <path d="M6 10.5h4m-2-2v4" />
    <path d="M14.5 9.75h.01" />
    <path d="M17.5 11.75h.01" />
    <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258a4 4 0 0 0-3.995-3.742z" />
  </Icon>
);

const IconWeb = () => (
  <Icon>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <ellipse cx="12" cy="12" rx="4" ry="9" />
  </Icon>
);

const IconDatabase = () => (
  <Icon>
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
    <path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3" />
  </Icon>
);

const IconStrategy = () => (
  <Icon>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </Icon>
);

const SERVICES = [
  {
    icon: <IconCode />,
    title: 'Custom software development',
    description:
      'Software built from the ground up to fit how your business works: mobile and desktop applications, backend systems, and the internal tools your team relies on every day.',
  },
  {
    icon: <IconWeb />,
    title: 'Web development',
    description:
      'Websites, web platforms, and web applications, designed and built end to end. We set up your hosting as well, providing a seamless launch experience that is fast, secure, and scalable as you grow.',
  },
  {
    icon: <IconDatabase />,
    title: 'Databases',
    description:
      'Custom databases built around your data, structured to query easily and scale cleanly. Your business keeps everything in one place and accesses it seamlessly.',
  },
  {
    icon: <IconAI />,
    title: 'AI integration & automation',
    description:
      'We build our own AI assistant, VAE, directly into your product. VAE is tailored to exactly what your product does. Inside your software, it can answer questions, guide users, automate repetitive tasks and processes, and take the routine work off your plate.',
  },
  {
    icon: <IconGame />,
    title: 'Game development',
    description:
      'Original games brought to life from concept to release. We cover the full range of game development, from custom assets and gameplay design to multiplayer and beyond, turning the game you have always dreamed of making into one which the world gets to play.',
  },
  {
    icon: <IconStrategy />,
    title: 'Product strategy & consulting',
    description:
      "Not sure where to start? We help you turn an idea into a clear, actionable plan. From scoping and feasibility to technical architecture, roadmapping, and prioritization. We guide the decisions that shape a product, so you build the right thing, in the right order, with minimal cost.",
  },
];

const Services: React.FC = () => {
  return (
    <SectionWrapper
      id="services"
      label="Services"
      heading="Solutions built to perform"
      sub="From AI integrations to full product development, we partner with businesses to design and ship software that works."
    >
      <div className="services-grid" style={styles.grid}>
        {SERVICES.map((s, i) => (
          <Service
            key={s.title}
            index={pad2(i)}
            icon={s.icon}
            title={s.title}
            description={s.description}
          />
        ))}
      </div>
    </SectionWrapper>
  );
};

export default Services;
